import { Alert, Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { InstallGuide } from '../components/InstallGuide';
import { PlayerPicker } from '../components/PlayerPicker';
import { useInvite, useJoin, useSignInWithPin } from '../hooks/useAuthActions';
import { HandleSchema, PinSchema } from '../schemas/auth.schema';
import { getErrorMessage } from '../services/api-client';
import { colors } from '../themes/theme';

type Mode = 'choose' | 'new' | 'returning' | 'installed';

/** Landing page for the shared invite link that gets texted to the group. */
export function Join() {
    const { code } = useParams<{ code: string }>();
    const navigate = useNavigate();
    const invite = useInvite(code);
    const join = useJoin();
    const signIn = useSignInWithPin();

    const [mode, setMode] = useState<Mode>('choose');
    const [handle, setHandle] = useState('');
    const [pin, setPin] = useState('');

    const handleCheck = HandleSchema.safeParse(handle);
    const pinCheck = PinSchema.safeParse(pin);

    if (invite.isPending) {
        return <Centered><Typography color="text.secondary">Checking the link…</Typography></Centered>;
    }

    if (invite.isError) {
        return (
            <Centered>
                <Stack spacing={2}>
                    <Typography variant="h3">Couldn&rsquo;t check that link</Typography>
                    <Alert severity="error">{getErrorMessage(invite.error)}</Alert>
                </Stack>
            </Centered>
        );
    }

    if (!invite.data.valid) {
        return (
            <Centered>
                <Stack spacing={2}>
                    <Typography variant="h3">Link not usable</Typography>
                    <Alert severity="warning">{invite.data.reason}</Alert>
                    <Typography color="text.secondary">
                        Ask whoever sent it for a fresh one.
                    </Typography>
                </Stack>
            </Centered>
        );
    }

    const { players, hangoutName } = invite.data;

    if (mode === 'installed') {
        return (
            <Centered>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="overline" sx={{ color: colors.safety }}>
                            You&rsquo;re in
                        </Typography>
                        <Typography variant="h2">One more thing</Typography>
                    </Box>
                    <InstallGuide intro="Put Pony Up on your home screen so it opens like an app and can notify you when a bet goes up." />
                    <Button variant="contained" size="large" onClick={() => navigate('/', { replace: true })}>
                        Take me to the board
                    </Button>
                </Stack>
            </Centered>
        );
    }

    return (
        <Centered>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="overline" sx={{ color: colors.safety }}>
                        Settle it
                    </Typography>
                    <Typography variant="h1" sx={{ fontSize: 'clamp(2.6rem, 14vw, 4.5rem)' }}>
                        Pony Up
                    </Typography>
                    <Typography color="text.secondary">
                        {hangoutName
                            ? `${hangoutName} is live. Pick a name and get in.`
                            : "The bets you're already making, with a scoreboard that remembers."}
                    </Typography>
                </Box>

                {mode === 'choose' && (
                    <Stack spacing={2}>
                        <Button variant="contained" size="large" onClick={() => setMode('new')}>
                            I&rsquo;m new here
                        </Button>
                        {players.length > 0 && (
                            <>
                                <Divider sx={{ color: colors.muted, fontSize: '0.72rem' }}>
                                    OR
                                </Divider>
                                <Button variant="outlined" color="inherit" size="large" onClick={() => setMode('returning')}>
                                    I&rsquo;ve played before
                                </Button>
                            </>
                        )}
                    </Stack>
                )}

                {mode === 'new' && (
                    <Stack
                        component="form"
                        spacing={2}
                        onSubmit={(event) => {
                            event.preventDefault();
                            if (!handleCheck.success || !pinCheck.success || !code) return;
                            join.mutate(
                                { code, handle: handle.trim(), pin },
                                { onSuccess: () => setMode('installed') },
                            );
                        }}
                    >
                        <TextField
                            label="What should we call you"
                            value={handle}
                            onChange={(event) => setHandle(event.target.value)}
                            error={handle.length > 0 && !handleCheck.success}
                            helperText={
                                handle.length > 0 && !handleCheck.success
                                    ? handleCheck.error.issues[0]?.message
                                    : 'This is what shows on the leaderboard'
                            }
                            autoComplete="off"
                            autoCapitalize="none"
                            autoFocus
                        />
                        <TextField
                            label="Pick a PIN"
                            value={pin}
                            onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))}
                            error={pin.length > 0 && !pinCheck.success}
                            helperText={
                                pin.length > 0 && !pinCheck.success
                                    ? pinCheck.error.issues[0]?.message
                                    : '4 digits. You only need it on a new phone.'
                            }
                            type="tel"
                            inputMode="numeric"
                            autoComplete="new-password"
                        />
                        {join.isError && <Alert severity="error">{getErrorMessage(join.error)}</Alert>}
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={!handleCheck.success || !pinCheck.success || join.isPending}
                        >
                            {join.isPending ? 'Setting up…' : 'Join'}
                        </Button>
                        <Button color="inherit" onClick={() => setMode('choose')}>
                            Back
                        </Button>
                    </Stack>
                )}

                {mode === 'returning' && (
                    <PlayerPicker
                        players={players}
                        pending={signIn.isPending}
                        error={signIn.isError ? getErrorMessage(signIn.error) : null}
                        onSubmit={(userId, enteredPin) =>
                            signIn.mutate(
                                { userId, pin: enteredPin },
                                { onSuccess: () => navigate('/', { replace: true }) },
                            )
                        }
                        onBack={() => setMode('choose')}
                    />
                )}
            </Stack>
        </Centered>
    );
}

function Centered({ children }: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                minHeight: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                px: 3,
                // Clear of the notch at the top and the home indicator below.
                pt: 'calc(32px + env(safe-area-inset-top))',
                pb: 'calc(32px + env(safe-area-inset-bottom))',
                maxWidth: 460,
                mx: 'auto',
            }}
        >
            {children}
        </Box>
    );
}
